import { Formik } from 'formik';
import { GraphQLFormattedError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	GraphQLErrorMessage,
	Heading,
	Input,
	Label,
	Paragraph,
	Separator,
	Spinner,
	Subheading,
} from '@treams/treams-ui';

type CreatedToken = {
	id: string;
	token: string;
};

export default function AccessToken() {
	const { t } = useTranslation();

	const query = graphql.usePersonalAccessTokenQuery({ fetchPolicy: 'cache-and-network' });

	const [create] = graphql.useCreatePersonalAccessTokenMutation();
	const [renew] = graphql.useRenewPersonalAccessTokenMutation();
	const [revoke, { loading: revoking }] = graphql.useRevokePersonalAccessTokenMutation();

	const [createdToken, setCreatedToken] = useState<CreatedToken | undefined>(undefined);
	const [error, setError] = useState<GraphQLFormattedError | undefined>(undefined);
	const [confirmRevoke, setConfirmRevoke] = useState(false);

	if (query.loading && !query.data) {
		return <Spinner centered />;
	}

	const existing = query.data?.viewer.personalAccessToken ?? null;

	const onCreate = async (password: string) => {
		setError(undefined);
		try {
			const { data } = await create({ variables: { password } });
			if (data) {
				setCreatedToken(data.createPersonalAccessToken);
			}
			await query.refetch();
		} catch (err) {
			setError(
				err.graphQLErrors?.[0] ?? {
					extensions: { error: { key: 'common.errors.offline' } },
				},
			);
		}
	};

	const onRenew = async (password: string) => {
		setError(undefined);
		try {
			const { data } = await renew({ variables: { password } });
			if (data) {
				setCreatedToken(data.renewPersonalAccessToken);
			}
			await query.refetch();
		} catch (err) {
			setError(
				err.graphQLErrors?.[0] ?? {
					extensions: { error: { key: 'common.errors.offline' } },
				},
			);
		}
	};

	const onRevoke = async () => {
		setError(undefined);
		try {
			await revoke();
			setConfirmRevoke(false);
			setCreatedToken(undefined);
			await query.refetch();
		} catch (err) {
			setError(
				err.graphQLErrors?.[0] ?? {
					extensions: { error: { key: 'common.errors.offline' } },
				},
			);
		}
	};

	return (
		<Container py={5}>
			<Box maxWidth={640}>
				<Heading>{t('personalSettings.accessToken.title')}</Heading>
				<Paragraph>{t('personalSettings.accessToken.description')}</Paragraph>
				<Separator my={4} />

				{error && (
					<Box mb={3}>
						<GraphQLErrorMessage error={error} />
					</Box>
				)}

				{createdToken && (
					<Callout color="green" mb={4}>
						<Box>
							<Subheading mb={2}>{t('personalSettings.accessToken.created.title')}</Subheading>
							<Paragraph mb={3}>{t('personalSettings.accessToken.created.description')}</Paragraph>
							<Input
								value={createdToken.token}
								onChange={() => undefined}
								inputProps={{ readOnly: true }}
							/>
						</Box>
					</Callout>
				)}

				{existing ? (
					<ExistingToken
						createdAt={existing.createdAt}
						confirmRevoke={confirmRevoke}
						revoking={revoking}
						onRenew={onRenew}
						onRevoke={onRevoke}
						onRequestRevoke={() => setConfirmRevoke(true)}
						onCancelRevoke={() => setConfirmRevoke(false)}
					/>
				) : (
					<CreateToken onCreate={onCreate} />
				)}
			</Box>
		</Container>
	);
}

function CreateToken({ onCreate }: { onCreate: (password: string) => Promise<void> }) {
	const { t } = useTranslation();

	return (
		<Formik
			initialValues={{ password: '' }}
			onSubmit={async ({ password }, helpers) => {
				await onCreate(password);
				helpers.resetForm();
			}}
		>
			<Flex flexDirection="column">
				<Subheading mb={2}>{t('personalSettings.accessToken.create.title')}</Subheading>
				<Paragraph mb={3}>{t('personalSettings.accessToken.create.description')}</Paragraph>
				<Box mb={3}>
					<Label mb={2}>{t('common.password')}:</Label>
					<Input.Formik type="password" name="password" />
				</Box>
				<Box>
					<Button.Formik title={t('personalSettings.accessToken.create.submit')} />
				</Box>
			</Flex>
		</Formik>
	);
}

function ExistingToken(props: {
	createdAt: string | Date;
	confirmRevoke: boolean;
	revoking: boolean;
	onRenew: (password: string) => Promise<void>;
	onRevoke: () => Promise<void>;
	onRequestRevoke: () => void;
	onCancelRevoke: () => void;
}) {
	const { t } = useTranslation();

	return (
		<Box>
			<Subheading mb={2}>{t('personalSettings.accessToken.existing.title')}</Subheading>
			<Paragraph mb={3}>
				{t('personalSettings.accessToken.existing.createdAt', {
					date: new Date(props.createdAt).toLocaleString(),
				})}
			</Paragraph>

			<Formik
				initialValues={{ password: '' }}
				onSubmit={async ({ password }, helpers) => {
					await props.onRenew(password);
					helpers.resetForm();
				}}
			>
				<Flex flexDirection="column">
					<Paragraph mb={3}>{t('personalSettings.accessToken.renew.description')}</Paragraph>
					<Box mb={3}>
						<Label mb={2}>{t('common.password')}:</Label>
						<Input.Formik type="password" name="password" />
					</Box>
					<Box mb={4}>
						<Button.Formik title={t('personalSettings.accessToken.renew.submit')} />
					</Box>
				</Flex>
			</Formik>

			<Separator my={4} />

			{props.confirmRevoke ? (
				<Callout color="red">
					<Box>
						<Subheading mb={2}>{t('personalSettings.accessToken.revoke.confirmTitle')}</Subheading>
						<Paragraph mb={3}>
							{t('personalSettings.accessToken.revoke.confirmDescription')}
						</Paragraph>
						<Flex>
							<Box mr={2}>
								<Button
									title={t('personalSettings.accessToken.revoke.confirm')}
									onClick={props.onRevoke}
									disabled={props.revoking}
								/>
							</Box>
							<Button type="secondary" title={t('common.cancel')} onClick={props.onCancelRevoke} />
						</Flex>
					</Box>
				</Callout>
			) : (
				<Button
					type="secondary"
					title={t('personalSettings.accessToken.revoke.request')}
					onClick={props.onRequestRevoke}
				/>
			)}
		</Box>
	);
}
